// src/pages/support/KnowledgeBase.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Switch,
    Grid,
    Alert,
    CircularProgress,
    MenuItem,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Search as SearchIcon,
    Visibility as VisibilityIcon,
    Label as LabelIcon
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';

const KnowledgeBase = () => {
    const [articles, setArticles] = useState([]);
    const [filteredArticles, setFilteredArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');

    // Editor de artículos
    const [editorDialogOpen, setEditorDialogOpen] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [currentArticle, setCurrentArticle] = useState({
        id: null,
        title: '',
        content: '',
        category: '',
        tags: [],
        published: true
    });
    const [tempTag, setTempTag] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Diálogo de confirmación de eliminación
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [articleToDelete, setArticleToDelete] = useState(null);

    const categories = [
        { value: 'general', label: 'Información General' },
        { value: 'technical', label: 'Soporte Técnico' },
        { value: 'billing', label: 'Facturación' },
        { value: 'installation', label: 'Instalación' },
        { value: 'faq', label: 'Preguntas Frecuentes' }
    ];

    useEffect(() => {
        fetchArticles();
    }, []);

    useEffect(() => {
        if (articles.length > 0) {
            filterArticles();
        }
    }, [searchTerm, categoryFilter, articles]);

    const fetchArticles = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch('/api/knowledge', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener artículos');
            }

            const data = await response.json();
            setArticles(data);
            setFilteredArticles(data);
        } catch (error) {
            console.error('Error fetching articles:', error);
            setError('Error al cargar los artículos');
        } finally {
            setLoading(false);
        }
    };

    const filterArticles = () => {
        let filtered = [...articles];

        // Filtro por categoría
        if (categoryFilter) {
            filtered = filtered.filter(article => article.category === categoryFilter);
        }

        // Filtro por búsqueda
        if (searchTerm) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(article =>
                article.title.toLowerCase().includes(search) ||
                article.content.toLowerCase().includes(search) ||
                (article.tags && article.tags.some(tag => tag.toLowerCase().includes(search)))
            );
        }

        setFilteredArticles(filtered);
    };

    const handleOpenEditor = (article = null) => {
        if (article) {
            setCurrentArticle({
                ...article,
                tags: article.tags || []
            });
            setIsEditing(true);
        } else {
            setCurrentArticle({
                id: null,
                title: '',
                content: '',
                category: '',
                tags: [],
                published: true
            });
            setIsEditing(false);
        }
        setEditorDialogOpen(true);
    };

    const handleCloseEditor = () => {
        setEditorDialogOpen(false);
        setCurrentArticle({
            id: null,
            title: '',
            content: '',
            category: '',
            tags: [],
            published: true
        });
    };

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        setCurrentArticle(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAddTag = () => {
        if (!tempTag.trim()) return;

        // Evitar duplicados
        if (!currentArticle.tags.includes(tempTag.trim())) {
            setCurrentArticle(prev => ({
                ...prev,
                tags: [...prev.tags, tempTag.trim()]
            }));
        }
        setTempTag('');
    };

    const handleRemoveTag = (tagToRemove) => {
        setCurrentArticle(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleSubmitArticle = async () => {
        // Validación básica
        if (!currentArticle.title || !currentArticle.content) {
            setError('El título y el contenido son obligatorios');
            return;
        }

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            const method = isEditing ? 'PUT' : 'POST';
            const url = isEditing
                ? `/api/knowledge/${currentArticle.id}`
                : '/api/knowledge';

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(currentArticle)
            });

            if (!response.ok) {
                throw new Error(`Error al ${isEditing ? 'actualizar' : 'crear'} artículo`);
            }

            // Cerrar editor y recargar artículos
            handleCloseEditor();
            await fetchArticles();
        } catch (error) {
            console.error('Error submitting article:', error);
            setError(`Error al ${isEditing ? 'actualizar' : 'crear'} artículo`);
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenDeleteDialog = (article) => {
        setArticleToDelete(article);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setArticleToDelete(null);
    };

    const handleDeleteArticle = async () => {
        if (!articleToDelete) return;

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/knowledge/${articleToDelete.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al eliminar artículo');
            }

            // Cerrar diálogo y recargar artículos
            handleCloseDeleteDialog();
            await fetchArticles();
        } catch (error) {
            console.error('Error deleting article:', error);
            setError('Error al eliminar artículo');
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenPreview = () => {
        setPreviewDialogOpen(true);
    };

    const handleClosePreview = () => {
        setPreviewDialogOpen(false);
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 90, valueGetter: (params) => params.row.id.slice(0, 8) },
        { field: 'title', headerName: 'Título', width: 250 },
        {
            field: 'category',
            headerName: 'Categoría',
            width: 150,
            valueFormatter: (params) => {
                const category = categories.find(cat => cat.value === params.value);
                return category ? category.label : params.value;
            }
        },
        {
            field: 'published',
            headerName: 'Publicado',
            width: 120,
            renderCell: (params) => (
                <Chip
                    label={params.value ? 'Publicado' : 'Borrador'}
                    color={params.value ? 'success' : 'default'}
                    size="small"
                />
            )
        },
        {
            field: 'tags',
            headerName: 'Etiquetas',
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {params.value && params.value.map((tag, index) => (
                        <Chip key={index} label={tag} size="small" />
                    ))}
                </Box>
            )
        },
        {
            field: 'updated_at',
            headerName: 'Actualizado',
            width: 150,
            valueFormatter: (params) => format(new Date(params.value), 'dd/MM/yyyy')
        },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 150,
            renderCell: (params) => (
                <Box sx={{ display: 'flex' }}>
                    <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenEditor(params.row)}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleOpenDeleteDialog(params.row)}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            )
        }
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Base de Conocimientos
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                <TextField
                    placeholder="Buscar artículos..."
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ flexGrow: 1 }}
                    InputProps={{
                        startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                />

                <TextField
                    select
                    label="Categoría"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ width: 200 }}
                >
                    <MenuItem value="">Todas</MenuItem>
                    {categories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                            {category.label}
                        </MenuItem>
                    ))}
                </TextField>

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenEditor()}
                >
                    Nuevo Artículo
                </Button>
            </Box>

            <DataGrid
                rows={filteredArticles}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                autoHeight
                loading={loading}
                getRowId={(row) => row.id}
            />

            {/* Editor de Artículos */}
            <Dialog open={editorDialogOpen} onClose={handleCloseEditor} maxWidth="md" fullWidth>
                <DialogTitle>
                    {isEditing ? 'Editar Artículo' : 'Nuevo Artículo'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1, pb: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Título"
                                    name="title"
                                    value={currentArticle.title}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Categoría"
                                    name="category"
                                    value={currentArticle.category}
                                    onChange={handleInputChange}
                                >
                                    <MenuItem value="">Selecciona una categoría</MenuItem>
                                    {categories.map((category) => (
                                        <MenuItem key={category.value} value={category.value}>
                                            {category.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={currentArticle.published}
                                            onChange={handleInputChange}
                                            name="published"
                                        />
                                    }
                                    label="Publicado"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Etiquetas"
                                    value={tempTag}
                                    onChange={(e) => setTempTag(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleAddTag} edge="end">
                                                    <AddIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    helperText="Presiona Enter para añadir etiquetas"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {currentArticle.tags.map((tag, index) => (
                                        <Chip
                                            key={index}
                                            label={tag}
                                            onDelete={() => handleRemoveTag(tag)}
                                            color="primary"
                                            variant="outlined"
                                            size="small"
                                            icon={<LabelIcon />}
                                        />
                                    ))}
                                </Box>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={15}
                                    label="Contenido (Markdown)"
                                    name="content"
                                    value={currentArticle.content}
                                    onChange={handleInputChange}
                                    required
                                    helperText="Puedes usar formato Markdown"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleOpenPreview} color="info" startIcon={<VisibilityIcon />}>
                        Vista Previa
                    </Button>
                    <Button onClick={handleCloseEditor}>Cancelar</Button>
                    <Button
                        onClick={handleSubmitArticle}
                        variant="contained"
                        color="primary"
                        disabled={submitting}
                        startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                        {isEditing ? 'Actualizar' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Vista Previa */}
            <Dialog open={previewDialogOpen} onClose={handleClosePreview} maxWidth="md" fullWidth>
                <DialogTitle>
                    Vista Previa: {currentArticle.title}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h5" gutterBottom>{currentArticle.title}</Typography>

                            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {currentArticle.category && (
                                    <Chip
                                        label={categories.find(c => c.value === currentArticle.category)?.label || currentArticle.category}
                                        color="primary"
                                        size="small"
                                    />
                                )}
                                {currentArticle.tags.map((tag, index) => (
                                    <Chip key={index} label={tag} size="small" />
                                ))}
                            </Box>

                            <Box sx={{ mt: 3 }}>
                                <ReactMarkdown>{currentArticle.content}</ReactMarkdown>
                            </Box>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClosePreview}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de Confirmación de Eliminación */}
            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que deseas eliminar el artículo "{articleToDelete?.title}"?
                        Esta acción no se puede deshacer.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancelar</Button>
                    <Button
                        onClick={handleDeleteArticle}
                        variant="contained"
                        color="error"
                        disabled={submitting}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default KnowledgeBase;